// src/pages/public/Demo.js — GATEON 데모 갤러리 (/demo)
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo, { breadcrumbLd } from '../../components/common/Seo';
import { Calendar, MapPin, ExternalLink } from 'lucide-react';
import { eventService } from '../../services/eventService';
import { formatDate } from '../../utils/format';
import Loading from '../../components/common/Loading';
import PublicHeader from '../../components/common/PublicHeader';
import PublicFooter from '../../components/common/PublicFooter';
import PageIntro from '../../components/common/PageIntro';
import './OrgSite.scss';

const Demo = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await eventService.getActiveEvents();
        if (!cancelled) setEvents(data || []);
      } catch (err) {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="org-site">
      <Seo
        title="행사 예매 페이지 예시 | 게이트온 데모"
        description="게이트온으로 만든 전시·축제 예매 페이지 예시를 둘러보세요. 행사 소개부터 티켓 선택, 예매 화면까지 실제와 같은 흐름으로 확인할 수 있습니다."
        path="/demo"
        jsonLd={breadcrumbLd([{ name: '홈', path: '/' }, { name: '데모', path: '/demo' }])}
      />
      <PublicHeader />

      <PageIntro
        title="게이트온으로 만든 예매 페이지"
        description="예시로 만든 행사 페이지입니다. 행사 소개부터 티켓 선택까지 관람객이 보는 화면을 그대로 둘러보세요."
        trail={[{ label: '데모' }]}
      />

      <main id="main" className="org-site__main">
        <div className="org-site__main-inner">
          <div className="org-site__section-head">
            <h2>예시 행사 목록</h2>
            <p>카드를 누르면 해당 행사의 예매 페이지로 이동합니다.</p>
          </div>

          {loading ? (
            <Loading />
          ) : events.length === 0 ? (
            <div className="org-site__empty">
              <Calendar size={48} strokeWidth={1.4} />
              <h3>곧 공개됩니다</h3>
              <p>예시 행사를 준비하고 있습니다.</p>
            </div>
          ) : (
            <div className="org-site__grid">
              {events.map((event) => (
                <Link
                  key={event.id}
                  to={`/e/${event.slug}`}
                  className="org-event-card"
                >
                  <div className="org-event-card__image">
                    {event.thumbnail_url ? (
                      <img src={event.thumbnail_url} alt={event.name} loading="lazy" decoding="async" />
                    ) : (
                      <div className="org-event-card__placeholder" />
                    )}
                  </div>
                  <div className="org-event-card__body">
                    <h3>{event.name}</h3>
                    <div className="org-event-card__meta">
                      <span><Calendar size={14} strokeWidth={2} /> {formatDate(event.start_date)} ~ {formatDate(event.end_date)}</span>
                      {event.location && <span><MapPin size={14} strokeWidth={2} /> {event.location}</span>}
                    </div>
                    <span className="org-event-card__cta">
                      예매 페이지 보기 <ExternalLink size={14} strokeWidth={2} aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default Demo;
